import { isCellAbsent, kindAt, type Draft } from "./programEdit.js";
import { hasActiveWeek } from "./programBlocks.js";

// Save-time checks for the scheduler-composer (spec §5.4): WARN-ONLY, never
// blocking — the sheet offers "Fix" / "Save anyway". Hard guards (empty name,
// day with zero slots, schema pre-flight) stay in useProgramEditor.save and
// keep blocking with the inline error; this layer only surfaces the things
// the server would happily accept but the athlete probably didn't mean.
//
// v1 checks:
//   - emptyCell: an ACTIVE week × day cell with no running exercise (the
//     session has slots, but every one of them is inactive that week).
//   - pctNoMax: an exercise prescribed in %MAX with no saved MAX for its
//     slug. Needs the caller to pass the maxes map (fetched best-effort at
//     save time); null = maxes unknown → the check is skipped, never guessed.

export type ProgramDraftWarning =
  | { code: "emptyCell"; week: number; dayIdx: number }
  | { code: "pctNoMax"; exerciseId: string };

export function validateProgramDraft(
  draft: Draft,
  maxKgBySlug: ReadonlyMap<string, number> | null,
): ProgramDraftWarning[] {
  const warnings: ProgramDraftWarning[] = [];

  for (let w = 0; w < draft.cycleWeeks; w++) {
    draft.sessions.forEach((session, dayIdx) => {
      if (session.slots.length === 0) return; // hard guard's territory
      const anyActive = session.slots.some((slot) => !slot.inactiveWeeks[w]);
      if (!anyActive) warnings.push({ code: "emptyCell", week: w, dayIdx });
    });
  }

  if (maxKgBySlug !== null) {
    const flagged = new Set<string>();
    for (const session of draft.sessions) {
      for (const slot of session.slots) {
        if (flagged.has(slot.exerciseId)) continue;
        if (maxKgBySlug.has(slot.exerciseId)) continue;
        if (!hasActiveWeek(slot)) continue;
        const usesPct = slot.setRows.some((row) =>
          row.cells.some(
            (_, w) =>
              !slot.inactiveWeeks[w] && !isCellAbsent(row, w) && kindAt(row, w) === "percent-of-max",
          ),
        );
        if (usesPct) {
          flagged.add(slot.exerciseId);
          warnings.push({ code: "pctNoMax", exerciseId: slot.exerciseId });
        }
      }
    }
  }

  return warnings;
}
